import React from 'react';
import { useTranslation } from 'react-i18next';

import { TARGET_STREAK } from '../../daily-quests-page.constants';

import { clamp, useAnimatedNumber } from './weekly-streak-progress-bar.utils';

interface IWeeklyStreakProgressBarProps {
  current: number;
  target?: number;
  animateDuration?: number;
  className?: string;
}

export const WeeklyStreakProgressBar = ({
  current,
  target = TARGET_STREAK,
  animateDuration = 600,
  className = '',
}: IWeeklyStreakProgressBarProps) => {
  const { t } = useTranslation();
  const widthPct = clamp((current / Math.max(1, target)) * 100, 0, 100);
  const animDays = useAnimatedNumber(current, animateDuration);
  const cut = 22;
  const isFull = widthPct >= 100;
  return (
    <div
      className={`relative w-full select-none ${className}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={target}
      aria-valuenow={Math.round(clamp(current, 0, target))}
      aria-label="Weekly streak progress"
    >
      <div className="mt-2 h-7 rounded-2xl bg-[linear-gradient(180deg,_#202066_0%,_#58588C_100%)] p-0.5">
        <div className="h-full w-full rounded-2xl">
          <div
            className={`relative h-full w-full overflow-hidden rounded-2xl bg-[linear-gradient(180deg,_#0A0A42_0%,_#020037_100%)] shadow-[inset_0_2px_0_0_rgba(255,255,255,0.15),_-2px_-2px_4px_0_rgba(0,0,0,0.25)]`}
          >
            <div
              className={[
                'absolute left-0 top-0 h-full',
                'bg-[linear-gradient(184deg,_#B36E1B_2.53%,_#373470_96.52%)]',
                'opacity-80 transition-[width] ease-out motion-reduce:transition-none',
                isFull
                  ? 'rounded-full'
                  : 'rounded-l-2xl [clip-path:polygon(0_0,100%_0,calc(100%-_var(--cut))_100%,0_100%)]',
              ].join(' ')}
              style={{
                width: `${Math.min(widthPct, 100)}%`,
                transitionDuration: `${animateDuration}ms`,
                ...(isFull ? {} : { ['--cut' as any]: `${cut}px` }),
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="stroke-black stroke-2 text-[14px] font-bold leading-none text-white [-webkit-text-stroke:1px_#020037]">
                {`${animDays}/${target}`}{' '}
                <span className="uppercase">{t('pages.dailyQuests.days')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
